import { useState, useRef, useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { X, Upload, Image as ImageIcon, Loader2, CheckCircle } from 'lucide-react'
import Button from '@/components/ui/Button'
import Alert from '@/components/ui/Alert'

interface ImageUploadProps {
  barterId: number
  images: BarterImage[]
  maxImages?: number
  onImagesChange?: () => void
  canEdit?: boolean  // Permission to upload/delete images
}

interface BarterImage {
  id: number
  barter_id: number
  image_url: string
  image_key: string
  file_size: number
  display_order: number
  uploaded_at: string
}

interface PreviewImage {
  id: string
  file: File
  preview: string
  status: 'uploading' | 'success' | 'error'
  error?: string
}

export default function ImageUpload({
  barterId,
  images = [],
  maxImages = 25,
  onImagesChange,
  canEdit = true  // Default to true for backward compatibility
}: ImageUploadProps) {
  const [error, setError] = useState('')
  const [uploading, setUploading] = useState(false)
  const [previewImages, setPreviewImages] = useState<PreviewImage[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)
  const queryClient = useQueryClient()

  // Cleanup preview URLs when component unmounts
  useEffect(() => {
    return () => {
      previewImages.forEach(preview => {
        URL.revokeObjectURL(preview.preview)
      })
    }
  }, [previewImages])

  // Resize image before upload
  const resizeImage = async (file: File): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = (e) => {
        const img = new Image()
        img.onload = () => {
          const canvas = document.createElement('canvas')
          const ctx = canvas.getContext('2d')

          if (!ctx) {
            reject(new Error('Failed to get canvas context'))
            return
          }

          // Calculate new dimensions
          const maxWidth = 1920
          const maxHeight = 1080
          let width = img.width
          let height = img.height

          if (width > maxWidth || height > maxHeight) {
            const ratio = Math.min(maxWidth / width, maxHeight / height)
            width = width * ratio
            height = height * ratio
          }

          canvas.width = width
          canvas.height = height

          // Draw and resize
          ctx.drawImage(img, 0, 0, width, height)

          // Convert to blob
          canvas.toBlob(
            (blob) => {
              if (blob) {
                resolve(blob)
              } else {
                reject(new Error('Failed to create blob'))
              }
            },
            file.type,
            0.85 // Quality
          )
        }
        img.onerror = () => reject(new Error('Failed to load image'))
        img.src = e.target?.result as string
      }
      reader.onerror = () => reject(new Error('Failed to read file'))
      reader.readAsDataURL(file)
    })
  }

  const uploadImage = async (file: File) => {
    try {
      // Validate file size (max 1MB)
      if (file.size > 1048576) {
        throw new Error('File size must be less than 1MB')
      }

      // Validate file type
      if (!file.type.match(/^image\/(jpeg|jpg|png|gif|webp)$/)) {
        throw new Error('Only JPEG, PNG, GIF, and WebP images are allowed')
      }

      // Resize image
      const resizedBlob = await resizeImage(file)

      // Create form data
      const formData = new FormData()
      formData.append('barter_id', barterId.toString())
      formData.append('image', resizedBlob, file.name)

      const token = localStorage.getItem('token')

      // Upload to server
      const response = await fetch('/api/barter-images', {
        method: 'POST',
        headers: {
          'Authorization': 'Bearer ' + token,
        },
        body: formData,
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.message || 'Upload failed')
      }

      return data
    } catch (err: any) {
      throw new Error(err.message || 'Upload failed')
    }
  }

  const deleteImage = async (imageId: number) => {
    try {
      const token = localStorage.getItem('token')

      const response = await fetch('/api/barter-images/' + imageId, {
        method: 'DELETE',
        headers: {
          'Authorization': 'Bearer ' + token,
        },
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.message || 'Delete failed')
      }

      return data
    } catch (err: any) {
      throw new Error(err.message || 'Delete failed')
    }
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return

    // Check max images limit
    if (images.length + previewImages.length + files.length > maxImages) {
      setError('Maximum ' + maxImages + ' images allowed')
      return
    }

    setError('')
    setUploading(true)

    // Create preview images
    const newPreviews: PreviewImage[] = Array.from(files).map(file => ({
      id: Math.random().toString(36).substring(7),
      file,
      preview: URL.createObjectURL(file),
      status: 'uploading' as const,
    }))

    setPreviewImages(prev => [...prev, ...newPreviews])

    // Upload each file and update its status
    for (let i = 0; i < newPreviews.length; i++) {
      const preview = newPreviews[i]
      try {
        await uploadImage(preview.file)

        // Update preview status to success
        setPreviewImages(prev =>
          prev.map(p => p.id === preview.id ? { ...p, status: 'success' as const } : p)
        )
      } catch (err: any) {
        // Update preview status to error
        setPreviewImages(prev =>
          prev.map(p =>
            p.id === preview.id
              ? { ...p, status: 'error' as const, error: err.message }
              : p
          )
        )
      }
    }

    // Refresh images and cleanup previews
    queryClient.invalidateQueries({ queryKey: ['barter-images', barterId] })
    if (onImagesChange) onImagesChange()

    // Clear successful previews after a short delay
    setTimeout(() => {
      setPreviewImages(prev => prev.filter(p => p.status === 'error'))
    }, 1000)

    setUploading(false)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const removePreview = (previewId: string) => {
    setPreviewImages(prev => {
      const preview = prev.find(p => p.id === previewId)
      if (preview) {
        URL.revokeObjectURL(preview.preview)
      }
      return prev.filter(p => p.id !== previewId)
    })
  }

  const handleDelete = async (imageId: number) => {
    if (!confirm('Are you sure you want to delete this image?')) return

    try {
      await deleteImage(imageId)
      queryClient.invalidateQueries({ queryKey: ['barter-images', barterId] })
      if (onImagesChange) onImagesChange()
    } catch (err: any) {
      setError(err.message)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <label className="label">
          <ImageIcon className="w-4 h-4 inline mr-2" />
          Images ({images.length + previewImages.filter(p => p.status !== 'error').length}/{maxImages})
        </label>
        {canEdit && (
          <>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading || images.length + previewImages.filter(p => p.status !== 'error').length >= maxImages}
            >
              <Upload className="w-4 h-4 mr-2" />
              {uploading ? 'Uploading...' : 'Upload Images'}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileChange}
              className="hidden"
            />
          </>
        )}
      </div>

      {error && <Alert type="error" message={error} onClose={() => setError('')} />}

      {uploading && (
        <div className="flex items-center space-x-2 text-sm text-primary-600">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>Uploading images...</span>
        </div>
      )}

      {(images.length > 0 || previewImages.length > 0) && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {/* Uploaded images */}
          {images.map((image) => (
            <div key={image.id} className="relative group">
              <img
                src={image.image_url}
                alt="Barter image"
                className="w-full h-32 object-cover rounded-lg border border-gray-300"
              />
              {canEdit && (
                <button
                  type="button"
                  onClick={() => handleDelete(image.id)}
                  className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}

          {/* Preview images being uploaded */}
          {previewImages.map((preview) => (
            <div key={preview.id} className="relative group">
              <img
                src={preview.preview}
                alt="Uploading..."
                className="w-full h-32 object-cover rounded-lg border border-gray-300"
              />

              {/* Upload status overlay */}
              <div className="absolute inset-0 bg-black bg-opacity-50 rounded-lg flex items-center justify-center">
                {preview.status === 'uploading' && (
                  <div className="text-white text-center">
                    <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
                    <p className="text-xs">Uploading...</p>
                  </div>
                )}
                {preview.status === 'success' && (
                  <div className="text-white text-center">
                    <CheckCircle className="w-8 h-8 mx-auto mb-2" />
                    <p className="text-xs">Uploaded!</p>
                  </div>
                )}
                {preview.status === 'error' && (
                  <div className="text-white text-center">
                    <X className="w-8 h-8 mx-auto mb-2" />
                    <p className="text-xs">Failed</p>
                  </div>
                )}
              </div>

              {/* Remove button for failed uploads */}
              {preview.status === 'error' && (
                <button
                  type="button"
                  onClick={() => removePreview(preview.id)}
                  className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full"
                >
                  <X className="w-4 h-4" />
                </button>
              )}

              {/* Error message tooltip */}
              {preview.status === 'error' && preview.error && (
                <div className="absolute bottom-2 left-2 right-2 bg-red-500 text-white text-xs p-2 rounded">
                  {preview.error}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {images.length === 0 && previewImages.length === 0 && !uploading && (
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
          <ImageIcon className="w-12 h-12 mx-auto text-gray-400 mb-2" />
          <p className="text-gray-600">No images uploaded yet</p>
          {canEdit && (
            <p className="text-sm text-gray-500 mt-1">
              Click "Upload Images" to add up to {maxImages} images
            </p>
          )}
        </div>
      )}

      <p className="text-xs text-gray-500">
        Maximum file size: 1MB per image. Supported formats: JPEG, PNG, GIF, WebP
      </p>
    </div>
  )
}
